// Vintage / Not Vintage vote bar for verified moderators reviewing another
// collector's verified post. Renders nothing unless:
//   - the viewer is signed in
//   - the viewer is a verified moderator
//   - the post isn't theirs
// Vote is upserted into moderator_votes (one row per (scan, moderator)),
// so toggling between options or re-voting replaces the prior value.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useModeratorStatus } from "@/hooks/useModeratorStatus";
import { toast } from "@/lib/toast";

type Vote = "vintage" | "not_vintage";

interface Props {
  scanId: string;
  ownerId: string;
}

interface Tally {
  vintage: number;
  not_vintage: number;
}

export function ModeratorVoteBar({ scanId, ownerId }: Props) {
  const { session } = useAuth();
  const { data: state } = useModeratorStatus();
  const viewerId = session?.user?.id;

  const isMyOwn = !!viewerId && viewerId === ownerId;
  const isApprovedMod = state?.status === "approved";
  const eligible = !isMyOwn && isApprovedMod;

  const [myVote, setMyVote] = useState<Vote | null>(null);
  const [tally, setTally] = useState<Tally>({ vintage: 0, not_vintage: 0 });
  const [busy, setBusy] = useState(false);

  const loadVotes = useCallback(async () => {
    if (!scanId) return;
    const { data, error } = await supabase
      .from("moderator_votes")
      .select("vote, moderator_id")
      .eq("scan_id", scanId);
    if (error) { console.warn("moderator_votes load:", error.message); return; }
    let v = 0, nv = 0;
    for (const row of (data ?? []) as Array<{ vote: Vote; moderator_id: string }>) {
      if (row.vote === "vintage") v++;
      else if (row.vote === "not_vintage") nv++;
      if (row.moderator_id === viewerId) setMyVote(row.vote);
    }
    setTally({ vintage: v, not_vintage: nv });
  }, [scanId, viewerId]);

  useEffect(() => { loadVotes(); }, [loadVotes]);

  if (!eligible) return null;

  const cast = async (vote: Vote) => {
    if (!viewerId || busy) return;
    setBusy(true);
    // Optimistic: bump tally locally, then upsert.
    const prev = myVote;
    setMyVote(vote);
    setTally((t) => {
      const next = { ...t };
      if (prev === "vintage") next.vintage = Math.max(0, next.vintage - 1);
      if (prev === "not_vintage") next.not_vintage = Math.max(0, next.not_vintage - 1);
      next[vote] += 1;
      return next;
    });
    const { error } = await supabase
      .from("moderator_votes")
      .upsert(
        { scan_id: scanId, moderator_id: viewerId, vote },
        { onConflict: "scan_id,moderator_id" },
      );
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Couldn't save vote");
      // Roll back the optimistic update by re-fetching.
      loadVotes();
      return;
    }
    toast.success(vote === "vintage" ? "Voted: vintage" : "Voted: not vintage");
  };

  const baseBtn: React.CSSProperties = {
    flex: 1,
    padding: "10px 12px",
    border: "1px solid hsl(var(--ink))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: busy ? "wait" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    opacity: busy ? 0.7 : 1,
  };

  return (
    <div style={{
      margin: "10px 0",
      padding: 10,
      background: "hsl(var(--paper-2))",
      border: "1px dashed hsl(var(--ink))",
    }}>
      <div className="caps" style={{
        fontSize: 9, color: "hsl(var(--ink-2))",
        letterSpacing: "0.12em", marginBottom: 8,
      }}>
        Moderator review · your take counts toward FIG's hive consensus.
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={() => cast("vintage")}
          disabled={busy}
          aria-pressed={myVote === "vintage"}
          style={{
            ...baseBtn,
            background: myVote === "vintage" ? "hsl(var(--olive))" : "hsl(var(--cream))",
            color: myVote === "vintage" ? "hsl(var(--cream))" : "hsl(var(--ink))",
          }}
        >
          <span>Vintage</span>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 18, height: 16, padding: "0 4px", borderRadius: 999,
            background: myVote === "vintage" ? "hsl(var(--cream) / 0.25)" : "hsl(var(--ink) / 0.1)",
            fontSize: 9, fontWeight: 700,
          }}>{tally.vintage}</span>
        </button>
        <button
          type="button"
          onClick={() => cast("not_vintage")}
          disabled={busy}
          aria-pressed={myVote === "not_vintage"}
          style={{
            ...baseBtn,
            background: myVote === "not_vintage" ? "hsl(var(--rust))" : "hsl(var(--cream))",
            color: myVote === "not_vintage" ? "hsl(var(--cream))" : "hsl(var(--ink))",
          }}
        >
          <span>Not vintage</span>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 18, height: 16, padding: "0 4px", borderRadius: 999,
            background: myVote === "not_vintage" ? "hsl(var(--cream) / 0.25)" : "hsl(var(--ink) / 0.1)",
            fontSize: 9, fontWeight: 700,
          }}>{tally.not_vintage}</span>
        </button>
      </div>
    </div>
  );
}
